import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import ForgotPasswordRequest from './pages/ForgotPasswordRequest';
import { ProgramProvider } from './ProgramContext';
import ResetPassword from './pages/ResetPassword';
import Activation from './pages/Activation';
import Welcome from './pages/Welcome';
import Login from './pages/Login';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import UserDashboard from './pages/userDashboard';
import SignOut from './pages/SignOut';
import Header from './components/Header';
import Footer from './components/Footer';
import { AuthProvider, AuthContext } from './AuthContext';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import useSignOut from './hooks/useSignOut';
import { LanguageContext, LanguageProvider } from './LanguageContext';
import Help from './pages/Help';
import Accessibility from './pages/Accessibility';
import Privacy from './pages/Privacy';
import Cookies from './pages/Cookies';
import About from './pages/About';
import AuthGuard from './AuthGuard';
// import StartJordan from './pages/StartJordan'; // removed unused
import Messages from './pages/Messages';
import ComposeMessage from './pages/ComposeMessage';
import MessageDetails from './pages/MessageDetails';
import Documents from './pages/Documents';
import DocumentDetail from './pages/DocumentDetail';
import DynamicTest from './pages/DynamicTest';
import SchemaPreview from './pages/SchemaPreview';
import SubmissionConfirmation from './pages/SubmissionConfirmation';
import SubmissionDetails from './pages/SubmissionDetails';
import { getRuntimeConfig } from './services/runtimeConfig';
import api from './axiosInstance';
import MaintenanceAnnouncementBanner from './components/MaintenanceAnnouncementBanner';
import { isMaintenanceAnnouncementVisible } from './utils/serviceAnnouncement';

const SERVICE_ANNOUNCEMENT_POLL_MS = 15 * 1000;

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.floor(parsed) : fallback;
};

function App() {
  // Only render providers and AppContent
  return (
    <AuthProvider>
      <LanguageProvider>
        <ProgramProvider>
          <Router>
            <AppContent />
          </Router>
        </ProgramProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const { isAuthenticated } = useContext(AuthContext) || { isAuthenticated: false };
  const location = useLocation();
  const signOut = useSignOut();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(300);
  const [warningTime, setWarningTime] = useState(30);
  const [secondsLeft, setSecondsLeft] = useState(300);
  const [serviceAnnouncement, setServiceAnnouncement] = useState(null);
  const [announcementNow, setAnnouncementNow] = useState(() => new Date());
  const timeoutRef = useRef();
  const warningRef = useRef();
  const autoLogoutRef = useRef(false);
  const showTimeoutWarningRef = useRef(showTimeoutWarning);

  useEffect(() => {
    showTimeoutWarningRef.current = showTimeoutWarning;
  }, [showTimeoutWarning]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const controller = new AbortController();
    const loadTimeoutConfig = async () => {
      try {
        const config = await getRuntimeConfig(controller.signal);
        const publicAuth = config?.authPublic || {};
        const combinedTokenTtl = publicAuth?.tokenTtl || config?.auth?.tokenTtl || {};
        const legacyTimeout = toNumber(combinedTokenTtl?.frontendIdle, 300);
        const absoluteLimit = toNumber(combinedTokenTtl?.absolute, null);
        let warningTrigger = toNumber(combinedTokenTtl?.warningTriggerSeconds, null);
        let warningCountdown = toNumber(combinedTokenTtl?.warningCountdownSeconds, null);
        if (warningCountdown == null) {
          warningCountdown = toNumber(config?.runtime?.intake?.session_timeout_warning_seconds, null);
        }
        if (warningCountdown == null) {
          warningCountdown = toNumber(combinedTokenTtl?.warningSeconds, null);
        }
        if (warningCountdown == null) {
          const computed = Math.floor(legacyTimeout * 0.1);
          warningCountdown = Math.max(5, Math.min(Math.max(5, legacyTimeout - 10), computed || 30));
        }
        if (warningTrigger == null) {
          warningTrigger = Math.max(0, legacyTimeout - warningCountdown);
        }
        let timeout = warningTrigger + warningCountdown;
        if (absoluteLimit != null && timeout > absoluteLimit) {
          timeout = absoluteLimit;
          if (warningCountdown >= timeout) {
            warningCountdown = timeout > 10 ? Math.max(5, timeout - 10) : Math.max(5, Math.floor(timeout / 2));
          }
          warningTrigger = Math.max(0, timeout - warningCountdown);
        }
        if (!cancelled) {
          setSessionTimeout(Math.max(10, timeout));
          setWarningTime(Math.max(5, warningCountdown));
          setSecondsLeft(Math.max(10, timeout));
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[session] failed to load session timeout config', err?.message || err);
        }
      }
    };
    loadTimeoutConfig();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!showTimeoutWarning) {
      setSecondsLeft(sessionTimeout);
    }
  }, [sessionTimeout, showTimeoutWarning]);

  const loadServiceAnnouncement = useCallback(async () => {
    try {
      const response = await api.get('/api/service-announcement/current');
      setServiceAnnouncement(response?.data && typeof response.data === 'object' ? response.data : null);
      return response?.data || null;
    } catch (err) {
      console.warn('[service-announcement] failed to load', err?.message || err);
      setServiceAnnouncement(null);
      return null;
    }
  }, []);

  useEffect(() => {
    loadServiceAnnouncement();
    const intervalId = window.setInterval(() => {
      loadServiceAnnouncement();
    }, SERVICE_ANNOUNCEMENT_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadServiceAnnouncement]);

  useEffect(() => {
    if (!isMaintenanceAnnouncementVisible(serviceAnnouncement)) {
      setAnnouncementNow(new Date());
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      setAnnouncementNow(new Date());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [serviceAnnouncement]);

  useEffect(() => {
    let ignoreActivity = false;
    if (!isAuthenticated) {
      setShowTimeoutWarning(false);
      showTimeoutWarningRef.current = false;
      return undefined;
    }
    const effectiveTimeout = Math.max(10, sessionTimeout);
    const effectiveWarning = Math.max(5, Math.min(effectiveTimeout - 1, warningTime));
    let countdownInterval;
    const resetTimer = () => {
      if (ignoreActivity) return;
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      autoLogoutRef.current = false;
      setShowTimeoutWarning(false);
      showTimeoutWarningRef.current = false;
      setSecondsLeft(effectiveTimeout);
      warningRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
        showTimeoutWarningRef.current = true;
        ignoreActivity = true;
        let count = effectiveWarning;
        setSecondsLeft(count);
        if (count > 0) {
          countdownInterval = setInterval(() => {
            count -= 1;
            setSecondsLeft(prev => (count >= 0 ? count : prev));
            if (count <= 0 && countdownInterval) {
              clearInterval(countdownInterval);
              countdownInterval = null;
            }
          }, 1000);
        }
      }, Math.max(0, (effectiveTimeout - effectiveWarning)) * 1000);
      timeoutRef.current = setTimeout(async () => {
        if (autoLogoutRef.current) return;
        autoLogoutRef.current = true;
        setShowTimeoutWarning(false);
        showTimeoutWarningRef.current = false;
        if (countdownInterval) {
          clearInterval(countdownInterval);
          countdownInterval = null;
        }
        await signOut({ redirectTo: '/sign-out', replace: true, hostedLogout: false });
        if (window.location.pathname !== '/sign-out') {
          window.location.assign('/sign-out');
        }
      }, effectiveTimeout * 1000);
    };
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    // Cognito mode heartbeat poll (lightweight) every 60s
    let hbInterval;
    import('./axiosInstance').then(({ default: api }) => {
      hbInterval = setInterval(async () => {
        try {
          const resp = await api.get('/api/auth/heartbeat');
          if (resp.data?.remainingAccessSeconds !== undefined) {
            // If token near expiry (<WARNING_TIME), surface warning
            if (resp.data.remainingAccessSeconds < effectiveWarning && !showTimeoutWarningRef.current) {
              setShowTimeoutWarning(true);
              showTimeoutWarningRef.current = true;
              setSecondsLeft(resp.data.remainingAccessSeconds);
            }
          }
        } catch (_) {}
      }, 60000);
    });
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      if (hbInterval) clearInterval(hbInterval);
    };
  }, [isAuthenticated, signOut, sessionTimeout, warningTime]);

  // Track last meaningful path (for post-auth return) excluding auth-specific pages
  useEffect(() => {
    const skip = ['/login','/register','/activate-account','/forgot-password','/reset-password','/sign-out'];
    skip.push('/change-password');
    if (!skip.includes(location.pathname)) {
      const full = location.pathname + (location.search || '') + (location.hash || '');
      sessionStorage.setItem('last_path', full);
    }
  }, [location]);

  const handleStay = () => {
    setShowTimeoutWarning(false);
    showTimeoutWarningRef.current = false;
    setSecondsLeft(sessionTimeout);
    window.location.reload();
  };
  const handleLogout = async () => {
    setShowTimeoutWarning(false);
    await signOut({ redirectTo: '/sign-out', replace: true, hostedLogout: false });
    if (window.location.pathname !== '/sign-out') {
      window.location.assign('/sign-out');
    }
  };

  return (
    <>
      <Header />
      <MaintenanceAnnouncementBanner announcement={serviceAnnouncement} now={announcementNow} />
      <SessionTimeoutWarning
        show={showTimeoutWarning}
        onStay={handleStay}
        onLogout={handleLogout}
        secondsLeft={secondsLeft}
        language={language}
      />
      <main className="govuk-main-wrapper govuk-width-container" role="main">
        <Routes>
          {/* Keep dev/test routes regardless of flag */}
          <Route path="/iset-wizard" element={<AuthGuard><DynamicTest /></AuthGuard>} />
          <Route path="/schema-preview" element={<SchemaPreview />} />
          {/* Feature-flagged main apply route: schema renderer vs legacy */}
          <Route path="/apply" element={<AuthGuard><DynamicTest /></AuthGuard>} />
          {/* ...existing routes... */}
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/activate-account" element={<Activation />} />
          <Route path="/forgot-password" element={<ForgotPasswordRequest />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/dashboard"
            element={<AuthGuard><UserDashboard suppressMessageBanner={isMaintenanceAnnouncementVisible(serviceAnnouncement)} /></AuthGuard>}
          />
          <Route path="/change-password" element={<AuthGuard><ChangePassword /></AuthGuard>} />
          <Route path="/sign-out" element={<SignOut />} />
          <Route path="/help" element={<Help />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/contact" element={<AuthGuard><Navigate to="/messages/new" replace /></AuthGuard>} />
          <Route path="/about" element={<About />} />
          <Route path="/messages" element={<AuthGuard><Messages /></AuthGuard>} />
          <Route path="/messages/new" element={<AuthGuard><ComposeMessage /></AuthGuard>} />
          <Route path="/messages/:messageId" element={<AuthGuard><MessageDetails /></AuthGuard>} />
          <Route path="/documents" element={<AuthGuard><Documents /></AuthGuard>} />
          <Route path="/documents/:id" element={<AuthGuard><DocumentDetail /></AuthGuard>} />
          <Route path="/application/:tracking_id" element={<AuthGuard><SubmissionDetails /></AuthGuard>} />
          <Route path="/submission/:tracking_id" element={<AuthGuard><SubmissionDetails /></AuthGuard>} />
          <Route path="/submission/confirmation" element={<AuthGuard><SubmissionConfirmation /></AuthGuard>} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

export default App;

